import { BuildingWideChangesDockingPanel } from "../docking-panels/buildingWideChangesDockingPanel";
import { CornersModelEditor } from "../editors/cornersModelEditor";
import { PanelsModelEditor } from "../editors/panelsModelEditor";
import { hooksToolbarGroupId, measureToolsToolbarGroupId } from "../toolbar/toolbarGroupIds";
import eventBus, { IApplicationEvent } from "../eventBus/eventDispatcher";

export type ForgeBuildingWideChangesExtensionLoadOptions = {
    panelsEditor: PanelsModelEditor;
    cornersEditor: CornersModelEditor;
};

export class ForgeBuildingWideChangesExtension extends Autodesk.Viewing.Extension {
    private panelsEditor: PanelsModelEditor;
    private cornersEditor: CornersModelEditor;
    private buildingWideChangesButton: Autodesk.Viewing.UI.Button | null = null;
    private dockingPanel: BuildingWideChangesDockingPanel | null = null;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ForgeBuildingWideChangesExtensionLoadOptions) {
        super(viewer, options);
        this.panelsEditor = options.panelsEditor;
        this.cornersEditor = options.cornersEditor;

        this.onWithoutCladdingMarkChange = this.onWithoutCladdingMarkChange.bind(this);
        this.onDockingPanelVisibilityChanged = this.onDockingPanelVisibilityChanged.bind(this);
        this.onToggleBWC = this.onToggleBWC.bind(this);
        this.onEscape = this.onEscape.bind(this);
    }

    load() {
        this.viewer.addEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.onEscape);
        eventBus.addEventListener("Dextall.BuildingWideChanges.SetPanelTypes.WithoutCladding", this.onWithoutCladdingMarkChange);

        return true;
    }

    unload() {
        eventBus.removeEventListener("Dextall.BuildingWideChanges.SetPanelTypes.WithoutCladding", this.onWithoutCladdingMarkChange);

        this.dockingPanel?.shutdown();

        this.buildingWideChangesButton?.removeEventListener("click", this.onToggleBWC);

        return true;
    }

    onToolbarCreated() {
        const toolbar = this.viewer.toolbar;
        let group = toolbar.getControl(hooksToolbarGroupId) as Autodesk.Viewing.UI.ControlGroup | undefined;

        if (!group) {
            const measureTools = toolbar.getControl(measureToolsToolbarGroupId) as
                | Autodesk.Viewing.UI.ControlGroup
                | undefined;

            if (!measureTools) {
                toolbar.addControl(new Autodesk.Viewing.UI.ControlGroup(measureToolsToolbarGroupId), { index: 1 });
            }

            group = new Autodesk.Viewing.UI.ControlGroup(hooksToolbarGroupId);
            toolbar.addControl(group, { index: toolbar.indexOf(measureToolsToolbarGroupId) });
        }

        this.buildingWideChangesButton = new Autodesk.Viewing.UI.Button("dextall-building-wide-changes-button");
        this.buildingWideChangesButton.setToolTip("Building Wide Changes");
        this.buildingWideChangesButton.setIcon("viewer-editor-building-wide-changes");

        this.buildingWideChangesButton.addEventListener("click", this.onToggleBWC);
        group.addControl(this.buildingWideChangesButton);

        this.dockingPanel = new BuildingWideChangesDockingPanel(
            this.viewer.container,
            this.onDockingPanelVisibilityChanged,
        );
    }

    private async setAllPanelTypesCladdingState(isChecked: boolean): Promise<void> {
        const panelsResult = await this.panelsEditor.batchUpdateTypes(isChecked);
        const cornersResult = await this.cornersEditor.batchUpdateTypes(isChecked);

        if (!panelsResult.isSuccess || !cornersResult.isSuccess) {
            const errorMessage = panelsResult.message || cornersResult.message || "An unknown error occurred.";
            this.notifyError(errorMessage);
            return;
        }

        const panelIds = panelsResult.items;
        const cornerIds = cornersResult.items;

        const undoRedo = async (flag: boolean) => {
            await this.panelsEditor.batchUpdateTypes(flag, panelIds);
            await this.cornersEditor.batchUpdateTypes(flag, cornerIds);
        };

        eventBus.dispatchEvent({
            type: "Dextall.UndoRedo.PushAction",
            payload: {
                action: {
                    name: isChecked
                        ? "Mark all panel and corner types without cladding"
                        : "Mark all panel and corner types with cladding",
                    undo: () => undoRedo(!isChecked),
                    redo: () => undoRedo(isChecked),
                },
            },
        });
        
        eventBus.dispatchEvent({
            type: "Dextall.BuildingWideChanges.ProcessComplete",
            payload: null,
        });
    }

    private onWithoutCladdingMarkChange(event: IApplicationEvent<boolean>) {
        this.setAllPanelTypesCladdingState(event.payload);
    }

    private notifyError(message: string) {
        eventBus.dispatchEvent({
            type: "Dextall.Common.Notify.Error",
            payload: message,
        });
    }

    private onDockingPanelVisibilityChanged(visibility: boolean) {
        const newState = visibility
            ? Autodesk.Viewing.UI.Button.State.ACTIVE
            : Autodesk.Viewing.UI.Button.State.INACTIVE;

        this.buildingWideChangesButton?.setState(newState);
    }

    private onToggleBWC() {
        const newState = !this.dockingPanel?.visible;
        this.dockingPanel?.setVisible(newState);
    }

    private onEscape() {
        this.dockingPanel?.setVisible(false);
    }
}

export const forgeBuildingWideChangesExtensionName = "Dextall.ForgeBuildingWideChangesExtension" as const;

Autodesk.Viewing.theExtensionManager.registerExtension(
    forgeBuildingWideChangesExtensionName,
    ForgeBuildingWideChangesExtension,
);
